import React, { ComponentProps, forwardRef, useEffect, useRef, useState } from 'react';
import { appSettings } from '@visalex/configs';
import { DayPicker, Direction, SimpleDropdown } from '@visalex/core';
import { DayPickerInputWrapper } from '@visalex/core/ui';
import { DateRange, DayPickerRangeProps } from 'react-day-picker';

import cx from 'clsx';
import endOfDay from 'date-fns/endOfDay';

import { Input } from '../../../Input/Input';
import { handleFromChangeRange } from '../../lib/handleFromChangeRange';
import { handleRangeSelect } from '../../lib/handleRangeSelect';
import { handleToChangeRange } from '../../lib/handleToChangeRange';
import { getRangeByName, Range, ranges } from '../../lib/selectCustomRangeHandler';
import { RangeChangeMeta, RangeValue } from '../../lib/types';
import { useCombineRangeInputValue } from '../../lib/useCombineRangeInputValue';
import { useControlledMonth } from '../../lib/useControlledMonth';
import { CustomRangeSelectList } from '../CustomRangeSelectList/CustomRangeSelectList';

import styles from './RangePickerInput.module.scss';

export type RangePickerProps = Omit<
  DayPickerRangeProps & {
    /** Is dayPicker disabled */
    isDisabled?: boolean;
    isRHFConnected?: boolean;
    // Validity of the field: valid=false = invalid, valid=null|undefined = not yet validated
    valid?: boolean | null;
    enablePortal?: boolean;

    size?: ComponentProps<typeof Input>['size'];
    // Date format example yyyy-mm-dd to show in input fields
    format?: string;
    dropdownTestId?: string;
    menuClassName?: string;

    direction?: Direction;
    // Value passed by RHF through props if connected
    value?: RangeValue;
    defaultValue?: DateRange;

    // Basic usage is to connect to RHF
    onChange?(range: DateRange): void;
    onBlur?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  },
  'mode'
>;

export const RangePickerInput = forwardRef<HTMLInputElement, RangePickerProps>(
  (
    {
      month,
      onMonthChange,
      isDisabled,
      onChange,
      value: valueFromRHF,
      format: dateFormat = appSettings.dateFormat,
      valid,
      enablePortal = false,
      size,
      defaultValue,
      dropdownTestId,
      isRHFConnected,
      direction = 'bottom',
      menuClassName,
      ...props
    },
    ref
  ) => {
    const [activeRange, setActiveRange] = useState<Range | null>(null);
    const portaledDayPickerRef = useRef<HTMLDivElement>(null);

    const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(props.selected || defaultValue);
    const { fromValue, toValue, setFromValue, setToValue } = useCombineRangeInputValue({
      valueFromRHF,
      setSelectedRange,
      setActiveRange,
      dateFormat,
      isRHFConnected,
      defaultValue,
    });

    const [isDayPickerOpen, setIsDayPickerOpen] = useState(false);
    const onDayPickerOpen = () => setIsDayPickerOpen(true);
    const onDayPickerClose = () => setIsDayPickerOpen(false);

    const [selectedMonth, setSelectedMonth] = useControlledMonth({
      defaultMonth: month,
      isDayPickerOpen,
      toValue,
      fromValue,
    });

    useEffect(() => {
      /** Connect inner state of react-day-picker with RHF and simple onChange */
      if ((fromValue && toValue) || (!fromValue && !toValue)) {
        onChange?.({
          from: selectedRange?.from,
          to: selectedRange?.to,
        });
      }
    }, [fromValue, toValue]);

    useEffect(() => {
      if (!selectedRange) return;

      const defaultRangeIndex = ranges
        .map(getRangeByName)
        .findIndex(
          (range) => Number(selectedRange.from) === Number(range.from) && Number(selectedRange.to) === Number(range.to)
        );

      if (defaultRangeIndex > -1) {
        setActiveRange(ranges[defaultRangeIndex]);
      }
    }, []);

    const meta: RangeChangeMeta = {
      activeRange,
      selectedRange,
      setToValue,
      setFromValue,
      setSelectedRange,
      dateFormat,
      setActiveRange,
      setSelectedMonth,
      onDayPickerClose,
    };

    return (
      <DayPickerInputWrapper
        isOpen={isDayPickerOpen}
        onDayPickerClose={onDayPickerClose}
        portaledElementRef={portaledDayPickerRef}
      >
        <div className={styles.input_wrapper}>
          <Input
            name="range_picker_input_1"
            size={size}
            placeholder="From Date"
            value={fromValue}
            onFocus={onDayPickerOpen}
            onChange={(_, e) => {
              handleFromChangeRange(e, meta);
              /** When manually selecting */
              setActiveRange(null);
            }}
            autoComplete="off"
            valid={valid}
            data-testid="range_picker_input_1"
            disabled={isDisabled}
            ref={ref}
          />
          <Input
            name="range_picker_input_2"
            size={size}
            placeholder="To Date"
            value={toValue}
            onFocus={onDayPickerOpen}
            onChange={(_, e) => {
              handleToChangeRange(e, meta);
              /** When manually selecting */
              setActiveRange(null);
            }}
            autoComplete="off"
            valid={valid}
            data-testid="range_picker_input_2"
            disabled={isDisabled}
          />
        </div>
        {!isDisabled && (
          <SimpleDropdown
            direction={direction}
            enablePortal={enablePortal}
            show={isDayPickerOpen}
            alignEnd={false}
            rootNode="#modal-root"
            menuClassName={cx(styles.menu, menuClassName)}
            tabIndex={-1}
            disableAnimation
            menuDataTestId={dropdownTestId}
          >
            <DayPicker
              mode="range"
              fixedWeeks
              showOutsideDays
              {...props}
              ref={portaledDayPickerRef}
              month={selectedMonth}
              selected={props.selected || selectedRange}
              numberOfMonths={typeof props.numberOfMonths !== 'undefined' ? props.numberOfMonths : 2}
              className={cx(styles.dayPicker, props.className)}
              classNames={{
                months: cx(styles.dayPicker__months, props.classNames?.months),
                root: cx(styles.dayPicker__root, props.classNames?.root),
                ...props.classNames,
              }}
              onMonthChange={(changedMonth) => {
                onMonthChange?.(changedMonth);
                setSelectedMonth(changedMonth);
              }}
              onSelect={(range, selectedDay, activeModifiers, e) => {
                if (range && range.to) {
                  range.to = endOfDay(range.to);
                }

                props.onSelect?.(range, selectedDay, activeModifiers, e);

                /** When manually selecting */
                setActiveRange(null);

                handleRangeSelect(range, meta);
              }}
            >
              <CustomRangeSelectList {...meta} />
            </DayPicker>
          </SimpleDropdown>
        )}
      </DayPickerInputWrapper>
    );
  }
);

RangePickerInput.displayName = 'RangePickerInput';
